import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Input } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faEdit,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { jobsApi } from "../../../../services/jobServices";
import { useJobDetails } from "../../../../providers/jobDetailsProvider";
import ConfirmationModal from "../../../ConfirmationModal";
import InformationModal from "../../../InformationModal";
import { utils } from "../../../../utils/utils";

const ProjectDetailsSummary = () => {
  const [jobDetails] = useJobDetails();
  const { job } = jobDetails;
  const jobId = job?.id;
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState("");
  const [projectNotes, setProjectNotes] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(
    utils.initConfirmationModal
  );
  const [informationModal, setInformationModal] = useState(
    utils.initInformationModal
  );

  useEffect(() => {
    const fetchProjectNotes = async () => {
      if (jobId) {
        const data = await jobsApi.getProjectNotes(jobId);
        if (data) {
          setProjectNotes(data);
          setNotes(data.note);
        }
      }
    };
    fetchProjectNotes();
  }, [jobId]);

  const handleSave = async () => {
    try {
      let data;
      if (projectNotes) {
        data = await jobsApi.updateProjectNotes(jobId, {
          ...projectNotes,
          note: notes,
        });
      } else {
        data = await jobsApi.createProjectNotes(jobId, { note: notes });
      }
      setProjectNotes(data);
      setNotes(data.note);
      setInformationModal({
        isOpen: true,
        title: "Update Project Notes",
        body: "Project notes updated successfully.",
        onClose: () => setInformationModal(utils.initInformationModal),
      });
    } catch (error) {
      setInformationModal({
        isOpen: true,
        title: "Error updating Project Notes",
        body: "There was an error updating the project notes.",
        onClose: () => setInformationModal(utils.initInformationModal),
      });
    } finally {
      setIsEditing(false);
      setConfirmationModal(utils.initConfirmationModal);
    }
  };

  const toggleEdit = async () => {
    if (isEditing) {
      setConfirmationModal({
        isOpen: true,
        title: "Update Project Notes",
        body: "Are you sure you want to save the changes?",
        onSubmit: handleSave,
        onClose: () => setConfirmationModal(utils.initConfirmationModal),
      });
    } else {
      setIsEditing(true);
    }
  };

  return (
    <Card className="m-0 h-100 p-3 d-flex flex-column">
      <CardHeader className="bg-white border-0 p-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center p-2 bg-soft-blue">
              <FontAwesomeIcon icon={faFileAlt} size="lg" />
            </div>
            <h4 className="mb-0 ml-3 font-weight-bold">Project details</h4>
          </div>
          <div className="cursor-pointer text-dark" onClick={toggleEdit}>
            <FontAwesomeIcon
              icon={isEditing ? faCheckCircle : faEdit}
              size="lg"
            />
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-0 pt-3 flex-grow-1 position-relative">
        {isEditing ? (
          <Input
            type="textarea"
            placeholder="Project notes"
            className="flex-grow-1 h-100"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        ) : (
          <div className="project-details-notes-container position-absolute project-notes-container d-flex flex-column">
            <p
              className={`text-muted m-0 project-notes-prewrap ${
                !notes ? "project-notes-placeholder" : ""
              }`}
            >
              {notes || "Project notes"}
            </p>
          </div>
        )}
      </CardBody>
      {confirmationModal.isOpen && <ConfirmationModal {...confirmationModal} />}
      {informationModal.isOpen && <InformationModal {...informationModal} />}
    </Card>
  );
};

export default ProjectDetailsSummary;
